import { useEffect, useRef, useState } from "react";
import { TopoBackground } from "@/components/height-map/TopoBackground";

/**
 * Scroll to zoom + right-click drag to pan (with fallback to Alt+left).
 * Content is always centered via absolute inset-0 flex, so tiny images
 * appear in the middle rather than pinned to the top.
 */
export const ZoomCanvas = ({
  children,
  className = "",
  minZoom = 1,
  maxZoom = 8,
  testId,
  seed = 1,
  withTopo = true,
}) => {
  const wrapperRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const pixelDelta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1);
      const zoomStep = clamp(-pixelDelta * 0.0015, -0.32, 0.32);
      const factor = Math.exp(zoomStep);
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;

      setZoom((prevZoom) => {
        const nextZoom = clamp(prevZoom * factor, minZoom, maxZoom);
        if (nextZoom === prevZoom) return prevZoom;
        const ratio = nextZoom / prevZoom;
        setPan((prevPan) => {
          if (nextZoom === 1) return { x: 0, y: 0 };
          return {
            x: prevPan.x * ratio + cx * (1 - ratio),
            y: prevPan.y * ratio + cy * (1 - ratio),
          };
        });
        return nextZoom;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [minZoom, maxZoom]);

  const onPointerDown = (e) => {
    // Right-click OR alt+left starts pan
    if (e.button === 2 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      dragRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPan({
      x: e.clientX - dragRef.current.x,
      y: e.clientY - dragRef.current.y,
    });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };
  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  return (
    <div
      ref={wrapperRef}
      data-testid={testId}
      className={`relative overflow-hidden ${className}`}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      onDoubleClick={reset}
    >
      {withTopo && <TopoBackground seed={seed} />}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
          transformOrigin: "center center",
          transition: dragRef.current
            ? "none"
            : "transform 110ms cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "transform",
        }}
      >
        {children}
      </div>
      <div className="absolute bottom-2 left-2 text-[10px] mono bg-black/50 text-zinc-300 px-2 py-1 rounded pointer-events-none">
        Scroll to zoom · Right-click drag to pan
        {zoom > 1.02 && <span> · {zoom.toFixed(1)}x</span>}
      </div>
      {zoom > 1.02 && (
        <button
          data-testid="zoom-reset"
          onClick={reset}
          className="absolute top-2 right-2 text-[10px] font-medium mono bg-black/60 backdrop-blur px-2 py-1 rounded text-zinc-200 hover:bg-black/80 transition"
        >
          {zoom.toFixed(1)}x · reset
        </button>
      )}
    </div>
  );
};
